import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import StopIcon from '@mui/icons-material/Stop'
import {
  campaignService,
  type CampaignDto,
  type CampaignParticipationDto,
  type CampaignRewardType,
  type CampaignStatus,
  type CampaignType,
  type CreateCampaignRequest,
  type DiscountScope,
} from '../api/adminServices'
import { useToast } from '../components/useToast'

const CAMPAIGN_TYPES: CampaignType[] = [
  'REFERRAL',
  'PHOTO_SUBMISSION',
  'VEHICLE_STICKER',
  'PROMO_CODE',
  'GENERIC',
]
const REWARD_TYPES: CampaignRewardType[] = [
  'DISCOUNT_PERCENTAGE',
  'DISCOUNT_AMOUNT',
  'FREE_PERIOD',
]
const SCOPES: DiscountScope[] = ['TOTAL', 'MONTHLY_FEE', 'USAGE']

const statusColor: Record<CampaignStatus, 'default' | 'success' | 'warning' | 'error'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  PAUSED: 'warning',
  ENDED: 'error',
}

const emptyForm: CreateCampaignRequest = {
  code: '',
  name: '',
  description: '',
  type: 'REFERRAL',
  rewardType: 'DISCOUNT_PERCENTAGE',
  rewardValue: undefined,
  rewardDurationDays: 30,
  discountScope: 'TOTAL',
  referredRewardValue: undefined,
  requiresApproval: false,
  maxRedemptions: undefined,
  maxRedemptionsPerAccount: 1,
  activate: false,
}

export default function CampaignsPage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [tab, setTab] = useState(0)
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState<CreateCampaignRequest>(emptyForm)

  const campaignsQuery = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignService.list(0, 100),
  })

  const participationsQuery = useQuery({
    queryKey: ['campaign-participations'],
    queryFn: () => campaignService.listParticipations(undefined, undefined, 0, 100),
    enabled: tab === 1,
  })

  const campaigns: CampaignDto[] = campaignsQuery.data?.content ?? campaignsQuery.data ?? []
  const participations: CampaignParticipationDto[] =
    participationsQuery.data?.content ?? participationsQuery.data ?? []

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['campaigns'] })
    void queryClient.invalidateQueries({ queryKey: ['campaign-participations'] })
  }

  const createMutation = useMutation({
    mutationFn: () => campaignService.create(form),
    onSuccess: () => {
      invalidate()
      toast.success('Campaign created')
      setCreateOpen(false)
      setForm(emptyForm)
    },
    onError: (e) => toast.errorFrom(e, 'Failed to create campaign'),
  })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CampaignStatus }) =>
      campaignService.setStatus(id, status),
    onSuccess: () => {
      invalidate()
      toast.success('Campaign updated')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to update campaign'),
  })

  const reviewMutation = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      campaignService.reviewParticipation(id, { approve }),
    onSuccess: () => {
      invalidate()
      toast.success('Participation reviewed')
    },
    onError: (e) => toast.errorFrom(e, 'Failed to review participation'),
  })

  const rewardLabel = (c: CampaignDto) => {
    if (c.rewardType === 'FREE_PERIOD') return `${c.rewardDurationDays ?? 0} free days`
    if (c.rewardType === 'DISCOUNT_PERCENTAGE') return `${c.rewardValue ?? 0}% off (${c.discountScope})`
    return `${c.rewardValue ?? 0} off (${c.discountScope})`
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4">Campaigns</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setForm(emptyForm)
            setCreateOpen(true)
          }}
        >
          New Campaign
        </Button>
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Campaigns" />
        <Tab label="Participations" />
      </Tabs>

      {tab === 0 && (
        <>
          {campaignsQuery.isLoading ? (
            <CircularProgress />
          ) : campaigns.length === 0 ? (
            <Alert severity="info">No campaigns yet. Create one to get started.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reward</TableCell>
                    <TableCell>Window</TableCell>
                    <TableCell>Redemptions</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.code}</TableCell>
                      <TableCell>{c.name}</TableCell>
                      <TableCell>{c.type}</TableCell>
                      <TableCell>{rewardLabel(c)}</TableCell>
                      <TableCell>
                        {(c.startDate ?? '—')} → {(c.endDate ?? '∞')}
                      </TableCell>
                      <TableCell>
                        {c.redemptionCount ?? 0}
                        {c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={c.status} color={statusColor[c.status]} />
                      </TableCell>
                      <TableCell align="right">
                        {c.status !== 'ACTIVE' && (
                          <Tooltip title="Activate">
                            <Button
                              size="small"
                              startIcon={<PlayArrowIcon />}
                              onClick={() => statusMutation.mutate({ id: c.id, status: 'ACTIVE' })}
                            >
                              Activate
                            </Button>
                          </Tooltip>
                        )}
                        {c.status === 'ACTIVE' && (
                          <Tooltip title="Pause">
                            <Button
                              size="small"
                              color="warning"
                              startIcon={<PauseIcon />}
                              onClick={() => statusMutation.mutate({ id: c.id, status: 'PAUSED' })}
                            >
                              Pause
                            </Button>
                          </Tooltip>
                        )}
                        {c.status !== 'ENDED' && (
                          <Tooltip title="End">
                            <Button
                              size="small"
                              color="error"
                              startIcon={<StopIcon />}
                              onClick={() => statusMutation.mutate({ id: c.id, status: 'ENDED' })}
                            >
                              End
                            </Button>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {participationsQuery.isLoading ? (
            <CircularProgress />
          ) : participations.length === 0 ? (
            <Alert severity="info">No participations yet.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Evidence</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Review</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participations.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell>{p.campaignName ?? p.campaignCode ?? p.campaignId}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {p.accountId}
                        </Typography>
                        {p.referredAccountId && (
                          <Typography variant="caption" color="text.secondary">
                            referred: {p.referredAccountId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {p.evidenceUrl ? (
                          <a href={p.evidenceUrl} target="_blank" rel="noreferrer">
                            view
                          </a>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{p.submittedAt ? p.submittedAt.slice(0, 10) : '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={p.status} />
                      </TableCell>
                      <TableCell align="right">
                        {p.status === 'PENDING' ? (
                          <>
                            <Tooltip title="Approve & apply reward">
                              <Button
                                size="small"
                                color="success"
                                startIcon={<CheckIcon />}
                                onClick={() => reviewMutation.mutate({ id: p.id, approve: true })}
                              >
                                Approve
                              </Button>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <Button
                                size="small"
                                color="error"
                                startIcon={<CloseIcon />}
                                onClick={() => reviewMutation.mutate({ id: p.id, approve: false })}
                              >
                                Reject
                              </Button>
                            </Tooltip>
                          </>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Create campaign dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Campaign</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Code"
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              helperText="Unique code (also used as promo/referral code)"
            />
            <TextField
              label="Name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label="Description"
              multiline
              rows={2}
              value={form.description ?? ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <TextField
              select
              label="Type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value as CampaignType })}
            >
              {CAMPAIGN_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Reward type"
              value={form.rewardType}
              onChange={(e) => setForm({ ...form, rewardType: e.target.value as CampaignRewardType })}
            >
              {REWARD_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
            {form.rewardType !== 'FREE_PERIOD' && (
              <>
                <TextField
                  label="Reward value"
                  type="number"
                  value={form.rewardValue ?? ''}
                  onChange={(e) =>
                    setForm({ ...form, rewardValue: e.target.value ? parseFloat(e.target.value) : undefined })
                  }
                />
                <TextField
                  select
                  label="Discount applies to"
                  value={form.discountScope}
                  onChange={(e) => setForm({ ...form, discountScope: e.target.value as DiscountScope })}
                >
                  {SCOPES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              </>
            )}
            <TextField
              label="Reward duration (days)"
              type="number"
              value={form.rewardDurationDays ?? ''}
              onChange={(e) =>
                setForm({ ...form, rewardDurationDays: e.target.value ? parseInt(e.target.value, 10) : undefined })
              }
            />
            {form.type === 'REFERRAL' && (
              <TextField
                label="Referred account reward value"
                type="number"
                value={form.referredRewardValue ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    referredRewardValue: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                helperText="Reward granted to the invited (new) account"
              />
            )}
            <TextField
              label="Max redemptions (total, blank = unlimited)"
              type="number"
              value={form.maxRedemptions ?? ''}
              onChange={(e) =>
                setForm({ ...form, maxRedemptions: e.target.value ? parseInt(e.target.value, 10) : undefined })
              }
            />
            <TextField
              label="Max redemptions per account"
              type="number"
              value={form.maxRedemptionsPerAccount ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  maxRedemptionsPerAccount: e.target.value ? parseInt(e.target.value, 10) : undefined,
                })
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(form.requiresApproval)}
                  onChange={(e) => setForm({ ...form, requiresApproval: e.target.checked })}
                />
              }
              label="Requires admin approval before reward"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(form.activate)}
                  onChange={(e) => setForm({ ...form, activate: e.target.checked })}
                />
              }
              label="Activate immediately"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.code.trim() || !form.name.trim() || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
